/**
 * Client-side image processing for Farmly media pipeline.
 * Handles downscaling, variant generation, and validation.
 */

import { ACCEPTED_MIME_TYPES, MAX_PIXELS, MAX_UPLOAD_BYTES, VARIANTS, type VariantWidth } from './mediaTypes';

// ── Error type ─────────────────────────────────────────

export class MediaUploadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MediaUploadError';
  }
}

// ── Validation ─────────────────────────────────────────

export function validateFile(file: File): void {
  if (!ACCEPTED_MIME_TYPES.includes(file.type as any)) {
    throw new MediaUploadError(
      `Filtypen "${file.type}" stöds inte. Använd JPEG, PNG eller WebP.`,
    );
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new MediaUploadError(
      `Filen är för stor (${(file.size / 1024 / 1024).toFixed(1)} MB). Max är 15 MB.`,
    );
  }
}

// ── Canvas helpers ─────────────────────────────────────

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new MediaUploadError('Kunde inte läsa bilden.'));
    };
    img.src = url;
  });
}

function scaleDown(
  img: HTMLImageElement,
  maxLongSide: number,
): { width: number; height: number } {
  const { naturalWidth: w, naturalHeight: h } = img;
  const longSide = Math.max(w, h);
  if (longSide <= maxLongSide) return { width: w, height: h };
  const ratio = maxLongSide / longSide;
  return {
    width: Math.round(w * ratio),
    height: Math.round(h * ratio),
  };
}

function canvasToWebpBlob(
  canvas: HTMLCanvasElement,
  quality = 0.82,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) return reject(new MediaUploadError('Kunde inte skapa bildfil.'));
        resolve(blob);
      },
      'image/webp',
      quality,
    );
  });
}

// ── Public API ─────────────────────────────────────────

/**
 * Downscale an image so its longest side is ≤ MAX_PIXELS.
 * Returns the original file if already within limits.
 */
export async function downscaleToMaxPixels(file: File): Promise<{
  img: HTMLImageElement;
  originalWidth: number;
  originalHeight: number;
}> {
  const img = await loadImage(file);
  const longSide = Math.max(img.naturalWidth, img.naturalHeight);
  if (longSide > MAX_PIXELS) {
    throw new MediaUploadError(
      `Bilden är för stor (${img.naturalWidth}×${img.naturalHeight}). Max ${MAX_PIXELS}px på längsta sidan.`,
    );
  }
  return {
    img,
    originalWidth: img.naturalWidth,
    originalHeight: img.naturalHeight,
  };
}

export interface VariantBlob {
  width: VariantWidth;
  blob: Blob;
}

/**
 * Create WebP variants at 320, 640, 1280 from an already-loaded image.
 * Only creates variants smaller than or equal to the original.
 */
export async function createWebpVariants(
  img: HTMLImageElement,
): Promise<VariantBlob[]> {
  const results: VariantBlob[] = [];
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new MediaUploadError('Canvas stöds inte i denna webbläsare.');

  for (const targetWidth of VARIANTS) {
    // Always generate all variants – scaleDown caps at original dimensions
    // so smaller originals just get duplicated at their native size.
    // This guarantees cover assets always have path_640 + path_1280.
    const dims = scaleDown(img, targetWidth);
    canvas.width = dims.width;
    canvas.height = dims.height;
    ctx.clearRect(0, 0, dims.width, dims.height);
    ctx.drawImage(img, 0, 0, dims.width, dims.height);

    const blob = await canvasToWebpBlob(canvas);
    results.push({ width: targetWidth, blob });
  }

  // All three variants are always generated (scaleDown handles capping).
  // No fallback needed – the loop above never skips.

  return results;
}
